import { useEffect, useRef, useState } from 'react'
import type { DetailsSection, DetailsSectionId } from './useDetailsSections'

/**
 * Welcher Bereich gerade im Bild steht. Rect-Vergleich gegen eine Aktivierungslinie,
 * gedrosselt über einen rAF-Latch. Kein IntersectionObserver — die Linie ist
 * erklärbar, Schwellen sind es nicht.
 *
 * **Die Linie liegt an der OBERKANTE, nicht ein Drittel tiefer.** Sie muss dort
 * liegen, wo auch der Sprung ausrichtet (`handleJump` setzt den Bereich an die
 * Oberkante): Sonst leuchtet nach einem Klick ein ANDERER Bereich als der
 * angesprungene. Gemeldet am 13.09.2026 für „Aufmerksamkeit" — der Bereich stand
 * oben, aber die Linie bei einem Drittel lag schon in „Tags", weil die letzten
 * drei Bereiche zusammen in das obere Drittel passen.
 *
 * Zwei Ränder braucht die Regel trotzdem:
 *
 * - **Am Ende der Rolle gewinnt der letzte Bereich.** Weiter geht es nicht, also
 *   steht man dort — auch wenn seine Überschrift die Oberkante nie erreicht.
 *   Ohne das wäre der unterste Bereich nie erreichbar, und sein Sprungziel
 *   leuchtete nach dem Klick auf einen anderen.
 * - **Ganz oben gewinnt der erste.** Dort ist noch keine Überschrift an der
 *   Kante vorbei; niemanden zu nennen hieße, die Leiste beim Aufbau leer zu
 *   lassen.
 *
 * Zwei Wachen, die der Vorlage in ProjectPage fehlen (dort unnötig, hier Pflicht):
 *
 * - **Instabile `sections`-Referenz**: `useDetailsSections` liefert ein
 *   `useMemo`-Array mit stabiler Identität, aber der Effekt hängt hier bewusst an
 *   einem aus den IDs abgeleiteten String (`sectionKey`) statt am Array selbst.
 *   Bekäme der Aufrufer je eine Aufgabe, die bei jedem Render ein neues Array baut,
 *   würde ein Array-Dependency den Effekt (Listener ab-/anmelden) bei jedem Render
 *   neu aufsetzen; der String bleibt gleich, solange sich die IDs nicht ändern. Die
 *   aktuellen `sections` liest `compute()` trotzdem immer frisch — über eine Ref.
 * - **Nullgroßer Container**: Inaktive Tabs bleiben mit `display: none` montiert
 *   (CLAUDE.md). Dort liefert `getBoundingClientRect()` nur Nullen — eine
 *   Aktivierungslinie bei `0/3` träfe zufällig irgendeinen Bereich. `compute()`
 *   bricht deshalb ab, wenn die gemessene Höhe 0 ist, und lässt `activeId`
 *   unverändert stehen.
 */
export function useSectionSpy(
  scrollRef: React.RefObject<HTMLElement | null>,
  sections: DetailsSection[],
): DetailsSectionId | null {
  const [activeId, setActiveId] = useState<DetailsSectionId | null>(null)
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  // Abgeleiteter Schlüssel statt der Array-Referenz: erspart dem Effekt ein
  // Neu-Anmelden des Scroll-Listeners, wenn der Aufrufer `sections` instabil hereinreicht.
  const sectionKey = sections.map(s => s.id).join('|')

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    let rafId: number | null = null

    const compute = () => {
      rafId = null
      const rect = container.getBoundingClientRect()
      // Inaktiver Tab (display: none) misst nur Nullen — nicht daraus rechnen.
      if (rect.height === 0) return

      const list = sectionsRef.current
      if (list.length === 0) {
        setActiveId(null)
        return
      }

      // Am Ende der Rolle gewinnt der letzte Bereich (siehe Brief oben).
      const remaining = container.scrollHeight - container.clientHeight - container.scrollTop
      if (remaining <= 1) {
        setActiveId(list[list.length - 1].id)
        return
      }

      // Ein Pixel Zugabe: Nach einem glatt gerollten Sprung liegt die Oberkante
      // des Bereichs auf Bruchteile genau auf der des Kastens, nicht darunter.
      const line = rect.top + 1
      let hit = list[0].id
      for (const section of list) {
        const el = container.querySelector<HTMLElement>(`[data-section="${section.id}"]`)
        if (el && el.getBoundingClientRect().top <= line) hit = section.id
      }
      setActiveId(hit)
    }

    const onScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(compute)
    }

    compute()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', onScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [scrollRef, sectionKey])

  return activeId
}
